import React, { useEffect, useState } from 'react';
import { Form, Input, Button, Spin } from 'antd';
import {
  SendOutlined, AuditOutlined, CalendarOutlined,
  ClockCircleOutlined, BellOutlined, InfoCircleOutlined,
  FireOutlined, AlertOutlined, WarningOutlined, CheckCircleOutlined,
} from '@ant-design/icons';
import axiosInstance from '../../variables/api/axiosInstance';
import { partieGarantie, adresseGarantie } from '../../services/partieGarantie';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import {
  formatDateDocument, langueDocument, langueVictime, tDocument,
} from '../../i18n/documents';
import { formatDate } from '../../variables/constants';
import { libelleDelai } from '../../i18n/donnees';

const GREEN = '#1a6b3c';

// Libellés traduits au rendu (ecran.relancePanel.niveaux).
const NIVEAU = {
  CRITIQUE:  { color: '#dc2626', bg: '#fee2e2', icon: <FireOutlined />        },
  URGENT:    { color: '#ea580c', bg: '#fff7ed', icon: <AlertOutlined />       },
  ATTENTION: { color: '#ca8a04', bg: '#fefce8', icon: <WarningOutlined />     },
  NORMAL:    { color: '#15803d', bg: '#f0fdf4', icon: <CheckCircleOutlined /> },
};

export default function RelancePanel({ notification, onClose, onSent }) {
  // Panneau dans la langue de l'agent ; sujet et message dans celle du destinataire.
  const { t } = useTranslation('documents');
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [contexte, setContexte] = useState(null);
  const [loadingCtx, setLoadingCtx] = useState(false);

  useEffect(() => {
    if (!notification) return;
    setLoadingCtx(true);
    setContexte(null);
    form.resetFields();
    detecterContexte(notification, t)
      .then(ctx => {
        setContexte(ctx);
        form.setFieldsValue({
          destinataire: ctx.destinataireSuggere || '',
          sujet: ctx.sujetSuggere,
          corps: ctx.corpsSuggere,
        });
      })
      .finally(() => setLoadingCtx(false));
  }, [notification, form, t]);

  const onSubmit = async (vals) => {
    if (!notification) return;
    setSubmitting(true);
    try {
      await axiosInstance.post(`/v1/delais/${notification.id}/relancer`, {
        destinataire: vals.destinataire?.trim() || null,
        sujet: vals.sujet?.trim() || null,
        corps: vals.corps || null,
      });
      onSent?.();
    } catch (e) {
      toast.error(e?.response?.data?.message || t('ecran.relance.erreurEnvoi'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!notification) return null;

  const codeNiveau = NIVEAU[notification.niveauAlerte] ? notification.niveauAlerte : 'NORMAL';
  const niv = { ...NIVEAU[codeNiveau], label: t(`ecran.relancePanel.niveaux.${codeNiveau}`) };

  return (
    <div style={{
      background: '#fff',
      display: 'flex', flexDirection: 'column',
    }}>

      {/* En-tête */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '14px 16px', borderBottom: '1px solid #e5e7eb', flexShrink: 0 }}>
        <SendOutlined style={{ color: GREEN, fontSize: 15 }} />
        <span style={{ flex: 1, fontSize: 15, fontWeight: 600, color: '#111827' }}>
          {t('ecran.relancePanel.titre')}{' '}
          <span style={{ fontFamily: 'ui-monospace,monospace', color: GREEN }}>{notification.sinistreNumeroLocal}</span>
        </span>
      </div>

      {/* Corps scrollable */}
      <div style={{ maxHeight: '65vh', overflowY: 'auto', padding: 16 }} className="rl-thin-sb">

        {/* Bandeau contexte délai */}
        <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, padding: '12px 14px', marginBottom: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#6b7280', fontWeight: 600 }}>
                {t('ecran.relance.delaiConcerne')}
              </div>
              <div style={{ fontSize: 13.5, fontWeight: 600, color: '#111827', marginTop: 2 }}>
                {libelleDelai(notification)}
              </div>
              {notification.referenceJuridique && (
                <span style={{ display: 'inline-block', marginTop: 6, fontFamily: 'ui-monospace,monospace', fontSize: 11, background: '#fef3c7', color: '#92400e', padding: '2px 8px', borderRadius: 5 }}>
                  <AuditOutlined style={{ marginRight: 4 }} />{notification.referenceJuridique}
                </span>
              )}
            </div>
            <span style={{
              fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 6,
              background: niv.bg, color: niv.color,
              display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap',
            }}>
              {React.cloneElement(niv.icon, { style: { fontSize: 10 } })} {niv.label}
            </span>
          </div>
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 12, color: '#374151' }}>
            <span>
              <CalendarOutlined style={{ marginRight: 4 }} />
              {t('ecran.relance.echeance')} <strong>{formatDate(notification.dateEcheance)}</strong>
            </span>
            <span style={{ color: notification.joursRestants <= 0 ? '#dc2626' : '#374151', fontWeight: notification.joursRestants <= 0 ? 600 : 400 }}>
              <ClockCircleOutlined style={{ marginRight: 4 }} />
              {notification.joursRestants <= 0
                ? t('ecran.relance.depasse', { jours: Math.abs(notification.joursRestants) })
                : t('ecran.relance.restants', { jours: notification.joursRestants })}
            </span>
            {notification.nombreAlertes > 0 && (
              <span style={{ color: '#6b7280' }}>
                <BellOutlined style={{ marginRight: 4 }} />
                {t('ecran.relance.dejaEnvoyees', { count: notification.nombreAlertes })}
              </span>
            )}
          </div>
        </div>

        {/* Chargement contexte */}
        {loadingCtx && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 0 6px', color: '#6b7280', fontSize: 12.5 }}>
            <Spin size="small" /> {t('ecran.relancePanel.analyse')}
          </div>
        )}

        {/* Résultats contexte */}
        {!loadingCtx && contexte && (
          <div style={{ marginBottom: 12 }}>
            {notification.categorie === 'PRESCRIPTION' && (
              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '8px 12px', marginBottom: 8, fontSize: 12, color: '#dc2626', fontWeight: 600 }}>
                {t('ecran.relancePanel.alertes.prescription')}
              </div>
            )}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {contexte.libelleCible && (
                <span style={{ fontSize: 11.5, background: '#f0fdf4', color: '#15803d', border: '1px solid #bbf7d0', padding: '3px 10px', borderRadius: 14 }}>
                  {t('ecran.relancePanel.vers', { cible: contexte.libelleCible })}
                </span>
              )}
              {contexte.manquants?.length > 0 && (
                <span style={{ fontSize: 11.5, background: '#fff7ed', color: '#ea580c', border: '1px solid #fed7aa', padding: '3px 10px', borderRadius: 14 }}>
                  {t('ecran.relancePanel.elementsManquants', { count: contexte.manquants.length })}
                </span>
              )}
            </div>
            {contexte.alerteContexte && (
              <div style={{ marginTop: 8, background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6, padding: '8px 12px', fontSize: 12, color: '#1d4ed8' }}>
                <InfoCircleOutlined style={{ marginRight: 6 }} />{contexte.alerteContexte}
              </div>
            )}
          </div>
        )}

        {/* Message pré-rempli dans la langue du destinataire : on le dit à l'agent. */}
        {!loadingCtx && contexte?.langueDocument === 'en' && (
          <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6, padding: '8px 12px', marginBottom: 12, fontSize: 12, color: '#1d4ed8' }}>
            <InfoCircleOutlined style={{ marginRight: 6 }} />{t('ecran.messageEnAnglais')}
          </div>
        )}

        {/* Note référencement automatique */}
        <div style={{
          background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6,
          padding: '8px 12px', marginBottom: 16, fontSize: 11.5, color: '#64748b',
          display: 'flex', alignItems: 'flex-start', gap: 6,
        }}>
          <InfoCircleOutlined style={{ marginTop: 1, flexShrink: 0 }} />
          <span>{t('ecran.relancePanel.referencement')}</span>
        </div>

        {/* Formulaire */}
        <Form form={form} layout="vertical" onFinish={onSubmit}>
          <Form.Item
            name="destinataire"
            label={<span style={{ fontSize: 12.5, fontWeight: 500 }}>{t('ecran.relance.destinataire')}</span>}
            extra={<span style={{ fontSize: 11, color: '#9ca3af' }}>{t('ecran.relancePanel.destinataireAide')}</span>}
            rules={[{ type: 'email', message: t('ecran.relance.emailInvalide') }]}
          >
            <Input placeholder="email@bureau.org" style={{ borderRadius: 6 }} />
          </Form.Item>
          <Form.Item
            name="sujet"
            label={<span style={{ fontSize: 12.5, fontWeight: 500 }}>{t('ecran.relance.sujet')} <span style={{ color: '#dc2626' }}>*</span></span>}
            rules={[{ required: true, message: t('ecran.relance.sujetRequis') }]}
          >
            <Input style={{ borderRadius: 6 }} />
          </Form.Item>
          <Form.Item
            name="corps"
            label={<span style={{ fontSize: 12.5, fontWeight: 500 }}>{t('ecran.relance.message')} <span style={{ color: '#dc2626' }}>*</span></span>}
            rules={[{ required: true, message: t('ecran.relance.messageRequis') }]}
          >
            <Input.TextArea
              rows={13}
              style={{ fontFamily: 'ui-monospace,monospace', fontSize: 12.5, lineHeight: 1.6, borderRadius: 6, background: '#fafafa' }}
            />
          </Form.Item>
        </Form>
      </div>

      {/* Pied */}
      <div style={{ padding: '12px 16px', borderTop: '1px solid #e5e7eb', background: '#fff', flexShrink: 0 }}>
        <Button
          type="primary" block
          loading={submitting}
          icon={<SendOutlined />}
          onClick={() => form.submit()}
          style={{ height: 40, background: GREEN, borderColor: GREEN, borderRadius: 6, fontSize: 14, fontWeight: 600 }}
        >
          {submitting ? t('ecran.relancePanel.envoiEnCours') : t('ecran.relance.envoyer')}
        </Button>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   Détection automatique du contexte - sans emojis

   Comme RelanceModal : `t` (langue de l'agent) pour ce que le panneau affiche,
   `d` (langue du destinataire) pour le sujet et le message. Le destinataire est un
   partenaire pour la garantie (compagnie ou homologue) et le recours (homologue),
   la victime pour l'offre (langue de sa fiche).
   ───────────────────────────────────────────────────────────────────────── */
export async function detecterContexte(n, t) {
  let destinataireSuggere = '';
  let libelleCible = '';
  let alerteContexte = null;
  let organismeDestinataire = null;
  const manquants = []; // fonctions (d) => texte : ils partent dans le message
  let corpsSpecifique = (d, sinistre, date, libelle) => d('relance.corps.defaut', { libelle, sinistre });

  let sinistreData = null;
  if (n.sinistreTrackingId) {
    try {
      const r = await axiosInstance.get(`/v1/sinistres/${n.sinistreTrackingId}`);
      sinistreData = r.data?.data;
    } catch { }
  }

  switch (n.categorie) {
    case 'GARANTIE': {
      // Même règle que RelanceModal : ET → bureau homologue, TE → compagnie.
      const partie = partieGarantie(sinistreData);
      if (partie.role === 'COMPAGNIE') {
        libelleCible = t('ecran.relance.cibles.compagnie', { nom: partie.libelle || '-' });
      } else if (n.homologueRaisonSociale || partie.libelle) {
        libelleCible = t('ecran.relance.cibles.homologue', { nom: n.homologueRaisonSociale || partie.libelle })
          + (n.homologueCodePays ? ` (${n.homologueCodePays})` : '');
      }
      organismeDestinataire = partie.organismeTrackingId;
      destinataireSuggere = sinistreData ? await adresseGarantie(sinistreData) : '';
      alerteContexte = partie.role === 'COMPAGNIE'
        ? t('ecran.relance.alertes.garantieCompagnie')
        : t('ecran.relance.alertes.garantieHomologue');
      corpsSpecifique = (d, sinistre) => d('relance.corps.garantie', { sinistre });
      break;
    }
    case 'PV':
      libelleCible = t('ecran.relance.cibles.provenancePv');
      if (sinistreData?.pvComplet === false) manquants.push((d) => d('relance.manquants.pvIncomplet'));
      alerteContexte = t('ecran.relance.alertes.pv');
      corpsSpecifique = (d, sinistre) => d('relance.corps.pv', { sinistre });
      break;
    case 'RESPONSABILITE':
      libelleCible = t('ecran.relance.cibles.assureurAvocat');
      if (sinistreData && (!sinistreData.positionRc || sinistreData.positionRc === 'EN_ATTENTE')) {
        manquants.push((d) => d('relance.manquants.positionRc'));
      }
      alerteContexte = t('ecran.relance.alertes.responsabilite');
      corpsSpecifique = (d, sinistre) => d('relance.corps.responsabilite', { sinistre });
      break;
    case 'EXPERTISE':
      libelleCible = t('ecran.relance.cibles.expert');
      alerteContexte = t('ecran.relancePanel.alertes.expertise');
      corpsSpecifique = (d, sinistre) => d('relance.corps.expertise', { sinistre });
      break;
    case 'OFFRE':
      libelleCible = n.victimeNomPrenom
        ? t('ecran.relance.cibles.victime', { nom: n.victimeNomPrenom })
        : t('ecran.relancePanel.cibles.victimeRepresentantLegal');
      alerteContexte = t('ecran.relance.alertes.offre');
      corpsSpecifique = (d, sinistre) => d('relance.corps.offre', { sinistre });
      break;
    case 'PAIEMENT':
      libelleCible = t('ecran.relancePanel.cibles.comptabilite');
      alerteContexte = t('ecran.relance.alertes.paiement');
      corpsSpecifique = (d, sinistre) => d('relance.corps.paiement', { sinistre });
      break;
    case 'RECOURS':
      if (n.homologueRaisonSociale) {
        libelleCible = n.homologueRaisonSociale + (n.homologueCodePays ? ` (${n.homologueCodePays})` : '');
        organismeDestinataire = n.homologueTrackingId;
      }
      alerteContexte = t('ecran.relancePanel.alertes.recours');
      corpsSpecifique = (d, sinistre) => d('relance.corps.recours', { sinistre });
      break;
    case 'PRESCRIPTION':
      alerteContexte = t('ecran.relancePanel.alertes.prescription');
      corpsSpecifique = (d, sinistre, date) => d('relance.corps.prescription', { sinistre, date });
      break;
    default:
      break;
  }

  let victime = null;
  if (n.victimeTrackingId) {
    try {
      const r = await axiosInstance.get(`/v1/victimes/${n.victimeTrackingId}`);
      const v = r.data?.data;
      victime = v;
      if (v?.statutVictime === 'ATTENTE_PIECES') {
        manquants.push((d) => d('relance.manquants.piecesVictime', { nom: n.victimeNomPrenom || '' }));
      }
    } catch { }
  }

  const langue = n.categorie === 'OFFRE' && victime
    ? langueVictime(victime)
    : await langueDocument(organismeDestinataire);
  const d = tDocument(langue);
  const libelle = libelleDelai(n, langue);
  const dateEcheance = formatDateDocument(n.dateEcheance, langue);
  const sinistre = n.sinistreNumeroLocal || d('relance.leDossier');
  const niveauLabel = ['CRITIQUE', 'URGENT', 'ATTENTION'].includes(n.niveauAlerte)
    ? d(`relance.niveauxCrochets.${n.niveauAlerte}`) : '';
  const lignesManquants = manquants.length > 0
    ? `\n\n${d('relance.elementsEnAttente')}\n` + manquants.map(m => `  • ${m(d)}`).join('\n')
    : '';
  const refLegale = n.referenceJuridique ? `\n\n${d('relance.reference')} ${n.referenceJuridique}` : '';

  const corpsSuggere = `${d('relance.bonjour')}

${corpsSpecifique(d, sinistre, dateEcheance, libelle)}

${d('relance.delai')} ${libelle}
${d('relance.echeance')} ${dateEcheance}${niveauLabel ? `\n${d('relance.niveau')} ${niveauLabel}` : ''}${lignesManquants}${refLegale}

${d('relance.remerciements')}

${d('relance.cordialement')}
${d('relance.signature')}`;

  const sujetSuggere = `[BNCB-CB]${niveauLabel ? ' ' + niveauLabel : ''} - ${libelle} - ${sinistre}`;

  return {
    destinataireSuggere,
    libelleCible,
    alerteContexte,
    manquants,
    sujetSuggere,
    corpsSuggere,
    langueDocument: langue,
  };
}
